/**
 * ShellStatusBoundary — renders session-level state banners.
 *
 * A-04: Distinguishes degraded/compromised/expired restoration states and
 * surfaces a recoverable retry affordance. Emits no sensitive data.
 */
import { Alert, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../features/auth/session-provider';
import { useBackendNavigation } from '../../runtime/navigation/use-navigation';

export function ShellStatusBoundary({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { restorationState, restorationReason, retry, isAuthenticated, loading } = useSession();
  const { data: nav, isLoading: navLoading } = useBackendNavigation();

  if (loading) return <>{children}</>;

  if (restorationState === 'compromised') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" role="alert" data-testid="shell-status-compromised">
          {t('session:compromised.message')}
          {restorationReason && (
            <Box component="span" sx={{ ml: 1, opacity: 0.7, fontSize: '0.85em' }}>
              ({restorationReason.code})
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  if (restorationState === 'degraded' && isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="warning"
          role="status"
          data-testid="shell-status-degraded"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              {t('common:retry')}
            </Button>
          }
        >
          {t('session:degraded.message')}
          {restorationReason && (
            <Box component="span" sx={{ ml: 1, opacity: 0.7, fontSize: '0.85em' }}>
              ({restorationReason.code})
            </Box>
          )}
        </Alert>
        {children}
      </Box>
    );
  }

  if (isAuthenticated && nav && Array.isArray(nav.primary) && nav.primary.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" role="status" data-testid="shell-status-no-workspace">
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t('session:noWorkspace.title')}
          </Box>
          {' — '}
          {t('session:noWorkspace.message')}
        </Alert>
        {children}
      </Box>
    );
  }

  return <>{children}</>;
}
